import React, { useState } from 'react';
import { App, Button } from 'antd';
import {
  CameraOutlined,
  CheckOutlined,
  EditOutlined,
  PrinterOutlined,
  ShoppingOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../../hooks/useResponsive';
import { useAppSelector } from '../../hooks/redux';
import { useGetOnboardingStatusQuery } from '../../services/onboardingApi';
import { useOpenCashRegisterMutation } from '../../services/cashRegisterApi';
import { useIsModuleEnabled } from '../../hooks/useIsModuleEnabled';
import { MODULE_KEYS } from '../../constants/moduleKeys';
import OpenCashRegisterModal from '../cashRegister/OpenCashRegisterModal';
import './onboardingChecklist.css';

interface Step {
  key: string;
  title: string;
  description: string;
  done: boolean;
  doneLabel: React.ReactNode;
  actions: React.ReactNode;
}

/**
 * "Primeros pasos": lo que le falta a un restaurante recién registrado para cobrar su
 * primera venta, con el botón que lo resuelve al lado. Vive arriba del dashboard, que
 * es donde aterriza el Administrador al terminar el registro, y desaparece sola cuando
 * hay menú y una venta cobrada: no hay "marcar como hecho", el servidor lee los hechos.
 *
 * Los tres pasos son el camino mínimo real: sin producto no se puede comandar, sin caja
 * a nombre de quien cobra el backend rechaza el pago, y "para llevar" no necesita mesas.
 * Mesas e impresora van aparte como opcionales porque no cuentan para terminar.
 */
const OnboardingChecklist: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { message } = App.useApp();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const [cashModalOpen, setCashModalOpen] = useState(false);

  // Se vuelve a pedir en cada montaje: el dueño va a cargar el menú, regresa, y el
  // paso tiene que aparecer tachado sin que nadie invalide nada a mano.
  const { data: status } = useGetOnboardingStatusQuery(undefined, {
    skip: !currentRestaurant?.id,
    refetchOnMountOrArgChange: true,
  });
  const { enabled: ingestEnabled } = useIsModuleEnabled(MODULE_KEYS.IngestaMenuIA);
  const [openCashRegister, { isLoading: isOpening }] = useOpenCashRegisterMutation();

  if (!status || status.completed) return null;

  const handleOpenCashRegister = async (initialAmount: number) => {
    try {
      await openCashRegister({ initialAmount }).unwrap();
      setCashModalOpen(false);
      message.success('Caja abierta. Ya puedes cobrar.');
    } catch (err: any) {
      message.error(err?.data?.message || err?.data?.error || 'No se pudo abrir la caja');
    }
  };

  const hasMenu = status.productsCount > 0;
  const steps: Step[] = [
    {
      key: 'menu',
      title: 'Carga tu menú',
      description: ingestEnabled
        ? 'Toma una foto de tu carta y la IA la captura por ti. También puedes escribirlo a mano.'
        : 'Crea una categoría y tus primeros productos.',
      done: hasMenu,
      doneLabel: (
        <>
          <span className="onb-step__count">{status.productsCount}</span>{' '}
          {status.productsCount === 1 ? 'producto' : 'productos'}
        </>
      ),
      actions: (
        <>
          {ingestEnabled && (
            <Button
              type="primary"
              icon={<CameraOutlined />}
              onClick={() => navigate('/menu-management?ingesta=1')}
            >
              Con foto de la carta
            </Button>
          )}
          <Button
            type={ingestEnabled ? 'default' : 'primary'}
            icon={<EditOutlined />}
            onClick={() => navigate('/menu-management')}
          >
            A mano
          </Button>
        </>
      ),
    },
    {
      key: 'cash',
      title: 'Abre tu caja',
      description: 'Para cobrar necesitas una caja abierta a tu nombre. Puede arrancar sin fondo.',
      done: status.hasOpenCashRegister,
      doneLabel: 'Caja abierta a tu nombre',
      actions: (
        <Button type={hasMenu ? 'primary' : 'default'} onClick={() => setCashModalOpen(true)}>
          Abrir caja
        </Button>
      ),
    },
    {
      key: 'sale',
      title: 'Cobra tu primera venta',
      description: 'Prueba con una venta para llevar: no necesita mesas. Puedes cancelarla después.',
      done: status.salesCount > 0,
      doneLabel: 'Primera venta cobrada',
      actions: (
        <Button
          type={hasMenu && status.hasOpenCashRegister ? 'primary' : 'default'}
          icon={<ShoppingOutlined />}
          onClick={() => navigate('/new-order/takeout')}
        >
          Nueva venta
        </Button>
      ),
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <section
      className={`onb-card${isMobile ? ' onb-card--mobile' : ''}`}
      aria-labelledby="onb-title"
    >
      <div className="onb-head">
        <div>
          <h2 id="onb-title" className="onb-title">Primeros pasos</h2>
          <p className="onb-subtitle">Tu restaurante puede estar cobrando en minutos.</p>
        </div>
        <span className="onb-progress" aria-label={`${doneCount} de ${steps.length} pasos listos`}>
          {doneCount} / {steps.length}
        </span>
      </div>

      <ol className="onb-steps">
        {steps.map((step, index) => (
          <li
            key={step.key}
            className={`onb-step${step.done ? ' onb-step--done' : ''}${isMobile ? ' onb-step--mobile' : ''}`}
          >
            <span className="onb-step__mark" aria-hidden="true">
              {step.done ? <CheckOutlined /> : index + 1}
            </span>
            <div className="onb-step__body">
              <p className="onb-step__title">{step.title}</p>
              {step.done ? (
                <p className="onb-step__done">{step.doneLabel}</p>
              ) : (
                <p className="onb-step__desc">{step.description}</p>
              )}
            </div>
            {!step.done && <div className="onb-step__actions">{step.actions}</div>}
          </li>
        ))}
      </ol>

      <div className="onb-later">
        <span>Cuando quieras:</span>
        <button type="button" className="onb-pill" onClick={() => navigate('/areas-tables')}>
          <TableOutlined />
          Mesas
          <span className="onb-pill__count">{status.tablesCount}</span>
        </button>
        <button type="button" className="onb-pill" onClick={() => navigate('/printer-settings')}>
          <PrinterOutlined />
          Impresora
          <span className="onb-pill__count">{status.printersCount}</span>
        </button>
      </div>

      <OpenCashRegisterModal
        visible={cashModalOpen}
        onCancel={() => setCashModalOpen(false)}
        onConfirm={handleOpenCashRegister}
        loading={isOpening}
      />
    </section>
  );
};

export default OnboardingChecklist;
